import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Clock, ArrowsClockwise } from "@phosphor-icons/react";

const ACTION_LABELS = {
  LOGIN: "Login",
  CREATE: "Criou",
  UPDATE: "Editou",
  DELETE: "Excluiu",
  BULK_DELETE: "Excluiu em lote",
  IMPORT: "Importou",
};

const RESOURCE_LABELS = {
  session: "sessão",
  user: "usuário",
  animal: "animal",
  cios: "cio",
  inseminacoes: "inseminação",
  partos: "parto",
  tratamentos: "tratamento",
  producoes: "produção",
  pesos: "pesagem",
};

const ACTION_TONE = {
  LOGIN: "bg-[#E9F5EC] text-[#2A5A3B] border-[#2A5A3B]/20",
  CREATE: "bg-[#E9F5EC] text-[#2A5A3B] border-[#2A5A3B]/20",
  UPDATE: "bg-[#FFF6E5] text-[#8B5A00] border-[#D4A017]/30",
  DELETE: "bg-[#FDEDED] text-[#C05746] border-[#C05746]/20",
  BULK_DELETE: "bg-[#FDEDED] text-[#C05746] border-[#C05746]/20",
  IMPORT: "bg-[#E9F5EC] text-[#2A5A3B] border-[#2A5A3B]/20",
};

function formatTimestamp(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR");
}

function detailsSummary(row) {
  const d = row.details || {};
  if (row.action === "UPDATE" && d.changes) {
    const keys = Object.keys(d.changes).slice(0, 4);
    return keys.map((k) => `${k}=${JSON.stringify(d.changes[k])}`).join(" · ");
  }
  if (row.action === "CREATE" && row.resource === "animal") {
    return `${d.numero || "-"} ${d.nome ? "· " + d.nome : ""} · ${d.grupo || ""}`;
  }
  if (row.action === "CREATE" && row.resource === "user") {
    return `${d.email || "-"} · ${d.role || ""}`;
  }
  if (row.action === "DELETE" && d.numero) return `${d.numero}${d.nome ? " · " + d.nome : ""}`;
  if (row.action === "BULK_DELETE") return `${d.count || 0} animais`;
  if (row.action === "IMPORT")
    return `criados ${d.created || 0} · ignorados ${d.skipped || 0} · erros ${d.errors || 0} · avisos ${d.warnings || 0}`;
  if (row.resource === "cios" || row.resource === "inseminacoes"
      || row.resource === "partos" || row.resource === "tratamentos"
      || row.resource === "producoes" || row.resource === "pesos") {
    if (d.data) return `data ${formatDate(d.data)}`;
  }
  return "";
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");

  async function load() {
    setLoading(true);
    const params = {};
    if (action) params.action = action;
    if (resource) params.resource = resource;
    const [logsRes, sumRes] = await Promise.all([
      api.get("/audit-logs", { params }),
      api.get("/audit-logs/summary").catch(() => ({ data: null })),
    ]);
    setLogs(logsRes.data);
    setSummary(sumRes.data);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [action, resource]);

  return (
    <div data-testid="audit-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock size={28} weight="duotone" color="#2A5A3B" />
          <div>
            <h1 className="font-heading font-black text-4xl tracking-tighter">
              Auditoria
            </h1>
            <p className="text-sm text-[#4E544B] mt-1">
              Registro de quem acessou e alterou informações da plataforma.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="audit-refresh"
          className="btn-secondary inline-flex items-center gap-2"
        >
          <ArrowsClockwise size={16} weight="bold" /> Atualizar
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-[#E2E0D8] rounded-sm p-4">
            <div className="text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
              Atividades (7 dias)
            </div>
            <div className="font-heading font-black text-3xl mt-1">{summary.total}</div>
          </div>
          {["LOGIN", "CREATE", "UPDATE", "DELETE"].map((a) => (
            <div key={a} className="bg-white border border-[#E2E0D8] rounded-sm p-4">
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
                {ACTION_LABELS[a]}
              </div>
              <div className="font-heading font-black text-3xl mt-1">
                {summary.by_action?.[a] || 0}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3">
        <select
          data-testid="filter-action"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="bg-white border border-[#E2E0D8] rounded-sm px-4 py-2.5 text-sm min-w-[180px]"
        >
          <option value="">Todas as ações</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          data-testid="filter-resource"
          value={resource}
          onChange={(e) => setResource(e.target.value)}
          className="bg-white border border-[#E2E0D8] rounded-sm px-4 py-2.5 text-sm min-w-[180px]"
        >
          <option value="">Todos os recursos</option>
          {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-[#E2E0D8] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F5F2] border-b border-[#E2E0D8]">
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Quem</th>
                <th className="px-4 py-3">Ação</th>
                <th className="px-4 py-3">Recurso</th>
                <th className="px-4 py-3">Detalhes</th>
              </tr>
            </thead>
            <tbody data-testid="audit-table">
              {loading && (
                <tr><td colSpan={5} className="text-center py-10 text-[#4E544B]">Carregando…</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-[#4E544B]">Nenhum registro encontrado.</td></tr>
              )}
              {!loading && logs.map((r) => (
                <tr key={r.id} className="border-t border-[#E2E0D8]">
                  <td className="px-4 py-3 whitespace-nowrap text-[#4E544B]">
                    {formatTimestamp(r.timestamp)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.user_name || "-"}</div>
                    <div className="text-[11px] text-[#4E544B]">{r.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${ACTION_TONE[r.action] || "border-[#E2E0D8] text-[#4E544B]"}`}>
                      {ACTION_LABELS[r.action] || r.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">{RESOURCE_LABELS[r.resource] || r.resource}</td>
                  <td className="px-4 py-3 text-[#4E544B] max-w-md truncate" title={detailsSummary(r)}>
                    {detailsSummary(r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
